import * as React from 'react';

import { Activity } from './interfaces';
import { ACTIVITY_FOCUS_FILTERS, FOCUS_GROUP_DESCRIPTORS, OTHER_FOCUS_GROUP_NAME, focusGroupOrder, } from './shared';

import { Checkbox, DISABLED, INDETERMINATE, } from '../../../../../Shared/index';

const dropdownIconSrc = `${window.postbuildEnv.CDN_URL}/images/icons/dropdown.svg`

interface ActivityFocusEntry {
  id: number,
  name: string,
}

interface ActivityFocusFilterRowProps {
  activityFocusFilters: number[],
  activityFocus: ActivityFocusEntry,
  handleActivityFocusFilterChange: (activityFocusFilters: number[]) => void,
  activityCount: number,
}

interface ActivityFocusGroupProps {
  groupName: string,
  focuses: ActivityFocusEntry[],
  activityFocusFilters: number[],
  handleActivityFocusFilterChange: (activityFocusFilters: number[]) => void,
  focusCountById: Map<number, number>,
}

interface ActivityFocusFiltersProps {
  activities: Activity[],
  filterActivities: (ignoredKey?: string) => Activity[]
  activityFocusFilters: number[],
  handleActivityFocusFilterChange: (activityFocusFilters: number[]) => void,
}

const ActivityFocusFilterRow = ({ activityFocusFilters, activityFocus, handleActivityFocusFilterChange, activityCount, }: ActivityFocusFilterRowProps) => {
  const selected = activityFocusFilters.includes(activityFocus.id)
  const disabled = activityCount === 0

  function checkIndividualFilter() {
    const newActivityFocusFilters = Array.from(new Set(activityFocusFilters.concat([activityFocus.id])))
    handleActivityFocusFilterChange(newActivityFocusFilters)
  }

  function uncheckIndividualFilter() {
    const newActivityFocusFilters = activityFocusFilters.filter(k => k !== activityFocus.id)
    handleActivityFocusFilterChange(newActivityFocusFilters)
  }

  function toggleCheckbox() {
    if (selected) { uncheckIndividualFilter() } else { checkIndividualFilter() }
  }

  return (
    <div className="individual-row filter-row activity-focus-row" key={activityFocus.id}>
      <div>
        <Checkbox
          ariaLabel={`Filter by ${activityFocus.name}`}
          onClick={toggleCheckbox}
          selected={selected}
          state={disabled ? DISABLED : null}
        />
        <span className="activity-focus-name">{activityFocus.name}</span>
      </div>
      <span>({activityCount})</span>
    </div>
  )
}

const ActivityFocusGroup = ({ groupName, focuses, activityFocusFilters, handleActivityFocusFilterChange, focusCountById, }: ActivityFocusGroupProps) => {
  const [isOpen, setIsOpen] = React.useState(false)

  function toggleIsOpen() { setIsOpen(!isOpen) }

  const filterSet = React.useMemo(() => new Set(activityFocusFilters), [activityFocusFilters])
  const focusIds = focuses.map(f => f.id)
  const selected = focusIds.some(id => filterSet.has(id))
  const topLevelActivityCount = focuses.reduce((acc, f) => acc + (focusCountById.get(f.id) ?? 0), 0)
  const disabled = topLevelActivityCount === 0
  const indeterminate = selected && !focusIds.every(id => filterSet.has(id))

  const checkboxState = indeterminate ? INDETERMINATE : disabled ? DISABLED : null

  function uncheckAllFilters() {
    const focusIdSet = new Set(focusIds)
    const newActivityFocusFilters = activityFocusFilters.filter(id => !focusIdSet.has(id))
    handleActivityFocusFilterChange(newActivityFocusFilters)
  }

  function checkAllFilters() {
    const newActivityFocusFilters = Array.from(new Set(activityFocusFilters.concat(focusIds)))
    handleActivityFocusFilterChange(newActivityFocusFilters)
  }

  function toggleTopLevelCheckbox() {
    if (selected) { uncheckAllFilters() } else { checkAllFilters() }
  }

  const toggleArrow = (
    <button aria-expanded={isOpen} aria-label={`${isOpen ? 'Hide' : 'Show'} ${groupName} foci`} className="interactive-wrapper focus-on-light filter-toggle-button" onClick={toggleIsOpen} type="button">
      <img alt="" className={isOpen ? 'is-open' : 'is-closed'} src={dropdownIconSrc} />
    </button>
  )

  const topLevelCheckbox = (
    <Checkbox
      ariaLabel={`Filter by ${groupName}`}
      onClick={toggleTopLevelCheckbox}
      selected={selected}
      state={checkboxState}
    />
  )

  const focusRows = isOpen ? focuses.map(focus => (
    <ActivityFocusFilterRow
      activityCount={focusCountById.get(focus.id) ?? 0}
      activityFocus={focus}
      activityFocusFilters={activityFocusFilters}
      handleActivityFocusFilterChange={handleActivityFocusFilterChange}
      key={focus.id}
    />
  )) : null

  const descriptor = FOCUS_GROUP_DESCRIPTORS[groupName]

  return (
    <section className="toggle-section activity-focus-toggle">
      <div className="top-level filter-row">
        <div>
          {toggleArrow}
          {topLevelCheckbox}
          <span>{groupName}{descriptor ? `: ${descriptor}` : ''}</span>
        </div>
        <span>({topLevelActivityCount})</span>
      </div>
      {focusRows}
    </section>
  )
}

const ActivityFocusFilters = ({ activities, filterActivities, activityFocusFilters, handleActivityFocusFilterChange, }: ActivityFocusFiltersProps) => {
  function clearAllActivityFocusFilters() { handleActivityFocusFilterChange([]) }

  const { groupedFocuses, sortedGroupNames } = React.useMemo(() => {
    const focusById = new Map<number, ActivityFocusEntry>()
    const groupByFocusId = new Map<number, string>()
    activities.forEach(a => {
      if (!a.activity_focus_id || !a.activity_focus_name) { return }
      if (!focusById.has(a.activity_focus_id)) {
        focusById.set(a.activity_focus_id, { id: a.activity_focus_id, name: a.activity_focus_name })
      }
      if (a.activity_focus_category_name) {
        groupByFocusId.set(a.activity_focus_id, a.activity_focus_category_name)
      }
    })

    const grouped = new Map<string, ActivityFocusEntry[]>()
    focusById.forEach((focus, id) => {
      const name = groupByFocusId.get(id) ?? OTHER_FOCUS_GROUP_NAME
      if (!grouped.has(name)) { grouped.set(name, []) }
      grouped.get(name).push(focus)
    })

    grouped.forEach((items, name) => {
      grouped.set(name, [...items].sort((a, b) => a.name.localeCompare(b.name)))
    })

    const groupNames = Array.from(grouped.keys()).sort((a, b) => {
      const oa = focusGroupOrder(a)
      const ob = focusGroupOrder(b)
      if (oa !== ob) { return oa - ob }
      return a.localeCompare(b)
    })

    return { groupedFocuses: grouped, sortedGroupNames: groupNames }
  }, [activities])

  const filteredActivities = filterActivities(ACTIVITY_FOCUS_FILTERS)

  const focusCountById = React.useMemo(() => {
    const counts = new Map<number, number>()
    filteredActivities.forEach(act => {
      const id = act.activity_focus_id
      if (id) { counts.set(id, (counts.get(id) ?? 0) + 1) }
    })
    return counts
  }, [filteredActivities])

  const groupSections = sortedGroupNames.map(groupName => (
    <ActivityFocusGroup
      activityFocusFilters={activityFocusFilters}
      focusCountById={focusCountById}
      focuses={groupedFocuses.get(groupName)}
      groupName={groupName}
      handleActivityFocusFilterChange={handleActivityFocusFilterChange}
      key={groupName}
    />
  ))

  const clearButton = activityFocusFilters.length
    ? <button className="interactive-wrapper clear-filter focus-on-light" onClick={clearAllActivityFocusFilters} type="button">Clear</button>
    : <span />

  return (
    <section aria-labelledby="activityFocusTitle" className="filter-section activity-focus-filter-section">
      <div className="name-and-clear-wrapper">
        <h2 id="activityFocusTitle">Activity Focus</h2>
        {clearButton}
      </div>
      {groupSections}
    </section>
  )
}

export default ActivityFocusFilters
